import React, { useId } from 'react';
import { Box, Button, Link, styled, Typography } from '@mui/material';
import { ReactComponent as CareProgramWelcome } from 'assets/icons/CareProgramWelcome.svg';
import { CloseButton } from 'components/CloseButton/CloseButton';
import { Drawer } from 'components/Drawer/Drawer';
import { Modal } from 'components/Modal/Modal';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

export interface CareProgramWelcomeSheetProps {
  open: boolean;
  onClose: () => void;
  /** Program-specific heading, e.g. "Welcome to the Asthma Support Program". */
  title: string;
  onGetStarted?: () => void;
  onBrowseCarePrograms?: () => void;
}

const CONTENT_MAX_WIDTH = 480;

const modalStyles = {
  width: '100%',
};

const drawerPaperStyles = {
  height: 'auto',
  maxHeight: '90%',
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
};

const ScgIconContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const DesktopModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(4),
  paddingInline: theme.spacing(6),
}));

const DesktopFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
}));

const WelcomeFooter = styled(Box)({
  width: '100%',
  maxWidth: CONTENT_MAX_WIDTH,
  marginInline: 'auto',
  display: 'flex',
  justifyContent: 'center',
});

const WelcomeBodyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  maxWidth: CONTENT_MAX_WIDTH,
  marginInline: 'auto',
  paddingTop: theme.spacing(4),
}));

const DrawerBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingInline: theme.spacing(6),
}));

const WelcomeTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

const StyledCloseButton = styled(CloseButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

export const CareProgramWelcomeSheet = ({
  open,
  onClose,
  title,
  onGetStarted,
  onBrowseCarePrograms,
}: CareProgramWelcomeSheetProps) => {
  const { isDesktop } = useDeviceType();
  const titleId = useId();
  const { translation } = useTranslationContext();
  const smallText = translation('care_program_small_text');
  const browsingText = translation('care_program_browsing_text');
  const browsingTextIndex = smallText.indexOf(browsingText);

  const browseLink = (
    <Link
      component="button"
      type="button"
      role="button"
      tabIndex={0}
      onClick={onBrowseCarePrograms}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onBrowseCarePrograms?.();
      }}
      aria-label={browsingText}
      sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
    >
      {browsingText}
    </Link>
  );

  const body = (
    <WelcomeBodyContainer>
      <WelcomeTitle color="primary" fontWeight={700} id={titleId} variant="h5">
        {title}
      </WelcomeTitle>
      <Typography color="grey.900" mb={1}>
        {translation('care_program_welcome_body_copy')}
      </Typography>
      <ScgIconContainer aria-hidden>
        <CareProgramWelcome width={240} />
      </ScgIconContainer>
      <Typography color="grey.900">
        {browsingTextIndex === -1 ? (
          smallText
        ) : (
          <>
            {smallText.slice(0, browsingTextIndex)}
            {browseLink}
            {smallText.slice(browsingTextIndex + browsingText.length)}
          </>
        )}
      </Typography>
    </WelcomeBodyContainer>
  );

  if (isDesktop) {
    return (
      <Modal
        modalTitle={title}
        open={open}
        onClose={onClose}
        modalTitleId={titleId}
        modalStyles={modalStyles}
      >
        <DesktopModalContent>
          <StyledCloseButton onClick={onClose} />
          {body}
          <DesktopFooter>
            <Button variant="contained" fullWidth color="secondary" onClick={onGetStarted}>
              {translation('care_program_welcome_cta')}
            </Button>
          </DesktopFooter>
        </DesktopModalContent>
      </Modal>
    );
  }
  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      sx={{ '& .MuiDrawer-paper': drawerPaperStyles }}
      ariaLabel={title}
      footer={
        <WelcomeFooter>
          <Button fullWidth variant="contained" color="secondary" onClick={onGetStarted}>
            {translation('care_program_welcome_cta')}
          </Button>
        </WelcomeFooter>
      }
    >
      <DrawerBody>{body}</DrawerBody>
    </Drawer>
  );
};
